import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import SwankyStyleSwatch from 'components/productdetail/swankyswatch/SwankyStyleSwatch';
import BeautySwatchGrouping from 'components/productdetail/swankyswatch/BeautySwatchGrouping';
import { showAirplaneSizing } from 'helpers/AirplaneSeatSizing';
import { getSwatchLegends, isColorOosForSelectedSizing } from 'helpers/SwankySwatchUtils';
import useMartyContext from 'hooks/useMartyContext';
import { FormattedProductBundle, ProductDetailState, StyleGroupings } from 'reducers/detail/productDetail';
import ProductUtils from 'helpers/ProductUtils';
import { AppState } from 'types/app';
import { ProductStyle } from 'types/cloudCatalog';
import { AirplaneCache } from 'types/AirplaneCache';
import Legend from 'components/productdetail/swankyswatch/Legend';
import { Legends } from 'components/productdetail/swankyswatch/LegendIndicator';

import css from 'styles/components/productdetail/swankyswatch/SwankySwatchBox.scss';

type OnChange<T> = (e: React.ChangeEvent<T>) => void;

const INITIAL_COLLAPSED_STYLES = 12;

export interface GivenProps {
  isGiftCard: boolean;
  onColorChange: (styleId: string, colorName?: string, isNew?: string) => void;
  productState: ProductDetailState;
  airplaneCache?: AirplaneCache;
  productType: string;
  selectedStyle: ProductStyle;
  styleList: ProductStyle[];
  collapsable?: boolean;
  isHydraColorLegend?: boolean;
  sizingChooser?: React.ReactNode;
  pageType: string;
}

export type SwankySwatchBoxProps = GivenProps & ReturnType<typeof mapStateToProps>;

function SwankySwatchBox(props: SwankySwatchBoxProps) {
  const {
    collapsable = false,
    isGiftCard,
    onColorChange,
    selectedStyle,
    styleList,
    airplaneCache,
    isHydraColorLegend,
    styleGroupings,
    productType,
    sizingChooser,
    pageType
  } = props;
  const { styleId: selectedStyleId } = selectedStyle;
  const isBeautyProduct = ProductUtils.isBeautyProduct(productType);
  const isBeautySwatch = isBeautyProduct && !!selectedStyle.swatchImageId;

  const onColorChangeForSwatches: OnChange<HTMLInputElement> = useCallback(
    e => {
      const {
        dataset: { colorName, styleId, isNew }
      } = e.target as HTMLInputElement;
      styleId && colorName && onColorChange(styleId, colorName, isNew);
    },
    [onColorChange]
  );

  const onColorChangeForDropdown: OnChange<HTMLSelectElement> = useCallback(
    e => {
      const { value } = e.target as HTMLSelectElement;
      if (value) {
        const newSelectedStyle = styleList.find(style => style.styleId === value);
        if (newSelectedStyle) {
          onColorChange(value, newSelectedStyle.color, newSelectedStyle.isNew);
        }
      }
    },
    [onColorChange, styleList]
  );

  let styleLabel = selectedStyle.specs ? 'Specs' : 'Color';
  let styleNamePrefix = '';
  if (isGiftCard) {
    styleLabel = 'Gift Amount';
    styleNamePrefix = '$';
  }

  const { testId } = useMartyContext();
  const [collapsed, setCollapsed] = useState(true);

  const toggleCollapse = () => setCollapsed(!collapsed);
  const legends = getSwatchLegends(styleList);
  const isShowLegend = legends.length > 0;

  const clonedStyleGroupings = JSON.parse(JSON.stringify(styleGroupings)); // deep copy styleGroupings due to nested array - avoid mutation
  const hasSubGroup =
    clonedStyleGroupings &&
    clonedStyleGroupings.some((grouping: { subgroups: string | any[] }) => grouping.subgroups && grouping.subgroups.length > 0);

  return (
    <div className={cn({ [css.isCollapsed]: collapsable && collapsed })}>
      <div className={css.colorText}>
        <span className={css.colorTextLabel}>{styleLabel}:</span>
        <span className={css.colorTextValue} data-test-id={testId('selectedColor')}>
          {styleNamePrefix}
          {selectedStyle.specs || selectedStyle.color}
        </span>
      </div>
      {isBeautyProduct && <div className={cn(css.colorText, { [css.beautySizeSpacing]: !hasSubGroup })}>{sizingChooser}</div>}
      {isHydraColorLegend && isShowLegend && <Legend legends={legends}></Legend>}
      <fieldset className={css.fieldset}>
        <legend className="sr-only">Select A {styleLabel}</legend>
        {styleGroupings?.length ? (
          <div>
            {clonedStyleGroupings?.map((group: StyleGroupings) => {
              const { name, subgroups, styleIds } = group;
              return (
                <div className={css.beautyGrouping} key={name}>
                  <p className={css.beautyGroupText}>{name}</p>
                  <BeautySwatchGrouping
                    styleList={styleList}
                    styleIds={styleIds}
                    subgroups={subgroups}
                    swankySwatchProps={props}
                    airplaneCache={airplaneCache}
                    onColorChangeForSwatches={onColorChangeForSwatches}
                    isBeautySwatch={isBeautySwatch}
                    pageType={pageType}
                  />
                </div>
              );
            })}
          </div>
        ) : isGiftCard ? (
          <select
            id="pdp-color-select"
            name="styleId"
            className={css.dropdown}
            data-test-id={testId('pdp-color')}
            onChange={onColorChangeForDropdown}
            value={selectedStyleId}
          >
            {styleList.map(({ styleId, color, isNew }) => (
              <option data-color-name={color} data-style-id={styleId} data-is-new={isNew} key={styleId} value={styleId}>
                {styleNamePrefix}
                {color}
              </option>
            ))}
          </select>
        ) : (
          <div className={cn(css.swatches, { [css.beautySwatches]: isBeautySwatch })} data-test-id={testId('styleSwatchContainer')}>
            {styleList.map(style => makeSwatch(props, airplaneCache, style, onColorChangeForSwatches, isBeautySwatch, pageType))}
          </div>
        )}
      </fieldset>
      {collapsable && styleList.length > INITIAL_COLLAPSED_STYLES && (
        <button className={css.collapseLink} type="button" onClick={toggleCollapse}>
          See {collapsed ? 'more' : 'less'} {isGiftCard ? 'options' : 'colors'}
        </button>
      )}
    </div>
  );
}

export function makeSwatch(
  props: SwankySwatchBoxProps,
  airplaneCache: AirplaneCache | undefined,
  style: ProductStyle,
  onColorChangeForSwatches: OnChange<HTMLInputElement>,
  isBeautySwatch: boolean,
  pageType: string
) {
  const { airplaneAllowList, alwaysAllowAirplane, productState, productType, selectedStyle, isHydraColorLegend } = props;

  const { detail: { brandName, productId } = {} as FormattedProductBundle } = productState;
  const { colorId, styleId, isNew } = style;

  let isInStock;
  if (showAirplaneSizing(airplaneAllowList, alwaysAllowAirplane, productType, airplaneCache) && !isBeautySwatch) {
    isInStock = airplaneCache.available.colorOptions.includes(colorId);
  } else {
    isInStock = !isColorOosForSelectedSizing(colorId, productState);
  }
  return (
    <SwankyStyleSwatch
      key={styleId}
      isSelected={styleId === selectedStyle.styleId}
      isUnavailable={!isInStock}
      onColorChange={onColorChangeForSwatches}
      productType={productType}
      isBeautySwatch={isBeautySwatch}
      style={style}
      brandName={brandName}
      namespace={`${productId}-${pageType}`}
      legendIndicator={isHydraColorLegend && isNew === 'true' ? Legends.New : undefined}
    />
  );
}

function mapStateToProps(store: AppState) {
  const {
    localStorage: {
      debugFlags: { alwaysAllowAirplane }
    },
    killswitch: { airplaneAllowList },
    product: { colorId, detail: { sizing, styleGroupings } = {} }
  } = store;
  return { airplaneAllowList, alwaysAllowAirplane, colorId, sizing, styleGroupings };
}

export default connect(mapStateToProps)(SwankySwatchBox);
